import { SupportedDailyWeeklyRentalTypes } from 'enums/supportedRentalTypes';
import { useMemo } from 'react';
import { dateService } from 'services/DateService';
import { VehicleBasedDailyWeeklyDemandState } from 'types/demand.type';
import { DailyWeeklyRentalInfo, ReservedTime } from 'types/vehicle.type';

export interface DateTimeOffset {
  hour: number;
  isOffset?: boolean;
  harborId?: number;
}

const WEEK_DAY_COUNT = 7;

export function usePriceWidgetDWReservedTimes({
  state,
  reservedTimes,
  rentalInfo,
}: {
  state: VehicleBasedDailyWeeklyDemandState;
  reservedTimes: ReservedTime[];
  rentalInfo: DailyWeeklyRentalInfo;
}) {
  const dayjs = dateService.getDayjs();
  const mappedReservedTimes: Date[] = useMemo(() => {
    const res: Date[] = [];
    reservedTimes.forEach((reservedTime) => {
      const {
        dateTimeRange: { endTime, startTime },
      } = reservedTime;
      const tourStartTime = dayjs(startTime);
      const tourEndTime = dayjs(endTime);
      const dayDifference = dateService.getDayDuration(
        tourStartTime.toDate(),
        tourEndTime.toDate()
      );

      if (state.demandVariant === SupportedDailyWeeklyRentalTypes.HALF_DAY) {
        [...Array(dayDifference + 1)].forEach((_, idx) => {
          res.push(tourStartTime.add(+idx, 'day').toDate());
        });
      } else if (
        state.demandVariant === SupportedDailyWeeklyRentalTypes.DAILY
      ) {
        if (dayDifference === 0) {
          res.push(tourStartTime.toDate());
        } else {
          [...Array(dayDifference)].forEach((_, idx) => {
            res.push(tourStartTime.add(+idx, 'day').toDate());
          });
        }
      } else if (
        state.demandVariant === SupportedDailyWeeklyRentalTypes.WEEKLY
      ) {
        // convery to dayjs format for computing ease
        const weekStartDay = dateService.getDayIndexAlignedWithDayjs(
          rentalInfo.checkInDay
        );

        [...Array(dayDifference)].forEach((_, idx) => {
          const currentDay = tourStartTime.add(+idx, 'day');
          if (+idx === 0) {
            const eventStartWeekDay = tourStartTime.day();
            const weekStartEventStartDiff = eventStartWeekDay - weekStartDay;
            let remainingWeekDayCount = weekStartEventStartDiff;
            if (remainingWeekDayCount < 0) {
              remainingWeekDayCount =
                WEEK_DAY_COUNT + remainingWeekDayCount - 1;
            }
            [...Array(remainingWeekDayCount)].forEach((_, idx) => {
              res.push(currentDay.subtract(+idx + 1, 'days').toDate());
            });
          }
          if (+idx === dayDifference - 1) {
            const eventEndWeekDay = tourEndTime.day();
            const weekStartEventEndDiff = eventEndWeekDay - weekStartDay;
            if (weekStartEventEndDiff > 0) {
              const remainingWeekDayCount =
                WEEK_DAY_COUNT - weekStartEventEndDiff - 1;
              [...Array(remainingWeekDayCount)].forEach((_, idx) => {
                res.push(currentDay.add(+idx + 1, 'days').toDate());
              });
            }
          }

          res.push(currentDay.toDate());
        });
      }
    });

    return res;
  }, [reservedTimes, dayjs, state.demandVariant]);

  return {
    mappedReservedTimes,
  };
}
